import { useMemo, useState } from "react"

import H1 from "~/ui-rtk/components/ui/typography/H1"
import {
  ExportButton,
  GranularitySelector,
  SegmentedToggle,
} from "~/ui-rtk/components/ui/controls"
import { AggregationType } from "~/ui-rtk/constants/charts"
import { deriveBucketEnd, resolveWeekStartDay } from "~/shared/kpi"
import { useCompanyControllerGetFinancialsQuery } from "~/ui-rtk/api/companyApi"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import useDate from "~/ui-rtk/hooks/date"
import { convertTableDataToCSV, downloadCSV } from "~/ui-rtk/utils/csv-export"

import useConnect, { type LtvCohortFilters } from "./connect"
import type { LtvMetricMode } from "./constants"
import CohortFilters from "./components/CohortFilters"
import CohortGrid from "./components/CohortGrid"
import HistoricalAverages from "./components/HistoricalAverages"
import PeriodSelector from "./components/PeriodSelector"
import {
  cohortCellValue,
  firstOrderValue,
  isCellMature,
  type CohortGranularity,
} from "./utils/cohort-matrix"
import {
  CUSTOM_PRESET_ID,
  DEFAULT_MONTHLY_PRESET_ID,
  DEFAULT_WEEKLY_PRESET_ID,
  addDaysIso,
  currentWeekStart,
  lastCompleteMonthEnd,
  monthlyPresets,
  weeklyPresets,
} from "./utils/period-presets"

const dayjs = useDate()

const METRIC_MODE_OPTIONS = [
  {
    value: "total",
    label: "Total LTV",
    title: "Accumulated value including the first order",
  },
  {
    value: "incremental",
    label: "Incremental LTV",
    title: "Repeat revenue only — first order excluded",
  },
]

const DEFAULT_FILTERS: LtvCohortFilters = {
  channels: [],
  valueBands: [],
  family: "none",
  productIds: [],
  collectionIds: [],
  skuPrefixLength: 3,
  skuPrefixes: [],
}

export default function LTVCohortsPage() {
  // Local calendar date (not UTC): maturity masking and "complete period"
  // boundaries must not flip early in the viewer's evening when UTC rolls.
  const todayIso = dayjs().format("YYYY-MM-DD")

  const currentCompany = useCurrentCompany()
  const { data: financials } = useCompanyControllerGetFinancialsQuery(
    { id: currentCompany?.id ?? "" },
    { skip: !currentCompany?.id },
  )
  const weekStartDay = resolveWeekStartDay(financials)

  const [aggType, setAggType] = useState<AggregationType>(
    AggregationType.MONTHLY,
  )
  const granularity: CohortGranularity =
    aggType === AggregationType.WEEKLY ? "week" : "month"

  const [presetId, setPresetId] = useState<string>(DEFAULT_MONTHLY_PRESET_ID)
  const [customRange, setCustomRange] = useState<{
    start: string
    end: string
  } | null>(null)
  const [mode, setMode] = useState<LtvMetricMode>("total")
  const [filters, setFilters] = useState<LtvCohortFilters>(DEFAULT_FILTERS)

  const presets = useMemo(
    () =>
      granularity === "month"
        ? monthlyPresets(todayIso)
        : weeklyPresets(todayIso, weekStartDay),
    [granularity, todayIso, weekStartDay],
  )

  // Custom ranges snap to complete reporting periods so cohort rows always
  // cover whole buckets (month or company reporting week). A pick lying
  // entirely inside the current incomplete period would invert after
  // clamping (end < start); collapse it to the last complete period instead
  // of sending Cube a contradictory dateRange.
  const snapCustomRange = (raw: { start: string; end: string }) => {
    if (granularity === "month") {
      const start = `${raw.start.slice(0, 7)}-01`
      const rawEnd = deriveBucketEnd(`${raw.end.slice(0, 7)}-01`, "month")
      const completeEnd = lastCompleteMonthEnd(todayIso)
      const end = rawEnd < completeEnd ? rawEnd : completeEnd
      return end < start
        ? { start: `${end.slice(0, 7)}-01`, end }
        : { start, end }
    }
    const start = currentWeekStart(raw.start, weekStartDay)
    const rawEnd = addDaysIso(currentWeekStart(raw.end, weekStartDay), 6)
    const completeEnd = addDaysIso(currentWeekStart(todayIso, weekStartDay), -1)
    const end = rawEnd < completeEnd ? rawEnd : completeEnd
    return end < start
      ? { start: currentWeekStart(end, weekStartDay), end }
      : { start, end }
  }

  const activePreset = presets.find(preset => preset.id === presetId)
  const range = useMemo(() => {
    if (presetId === CUSTOM_PRESET_ID && customRange) return customRange
    if (activePreset) {
      return { start: activePreset.start, end: activePreset.end }
    }
    // Granularity just changed and the old preset id is unknown here.
    const fallback = presets[0]
    return { start: fallback.start, end: fallback.end }
  }, [presetId, customRange, activePreset, presets])

  const handleGranularityChange = (next: AggregationType) => {
    if (next === aggType) return
    setAggType(next)
    setCustomRange(null)
    setPresetId(
      next === AggregationType.WEEKLY
        ? DEFAULT_WEEKLY_PRESET_ID
        : DEFAULT_MONTHLY_PRESET_ID,
    )
  }

  const {
    cohortRows,
    offsets,
    isLoading,
    error,
    productOptions,
    collectionOptions,
    skuPrefixOptions,
  } = useConnect({ granularity, range, filters })

  const handleExport = () => {
    const periodNoun = granularity === "month" ? "Month" : "Week"
    const columnConfig = [
      { key: "cohort", header: "First order at", type: "text" },
      { key: "newCustomers", header: "New customers", type: "number" },
      { key: "firstOrder", header: "First order", type: "currency" },
      ...offsets.map(offset => ({
        key: `offset_${offset}`,
        header: `${periodNoun} ${offset}`,
        type: "currency",
      })),
    ]
    const rows = cohortRows.map(row => {
      const record: Record<string, unknown> = {
        cohort:
          granularity === "month"
            ? dayjs(row.cohortStart).format("MMM YYYY")
            : row.cohortStart,
        newCustomers: row.newCustomers,
        firstOrder: firstOrderValue(row),
      }
      offsets.forEach(offset => {
        record[`offset_${offset}`] = isCellMature(
          row.cohortStart,
          offset,
          granularity,
          todayIso,
        )
          ? cohortCellValue(row, offset, mode)
          : null
      })
      return record
    })
    downloadCSV(
      convertTableDataToCSV(rows, columnConfig),
      `ltv-cohorts-${granularity}-${range.start}-${range.end}.csv`,
    )
  }

  return (
    <div className="h-full px-4 py-2 flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <H1>LTV Cohorts</H1>
        <div className="flex flex-wrap items-center gap-3">
          <PeriodSelector
            granularity={granularity}
            presets={presets}
            presetId={presetId}
            range={range}
            onPresetChange={id => {
              setPresetId(id)
              if (id === CUSTOM_PRESET_ID && !customRange) {
                setCustomRange(range)
              }
            }}
            onCustomRange={raw => setCustomRange(snapCustomRange(raw))}
          />
          <GranularitySelector
            value={aggType}
            onChange={handleGranularityChange}
            options={[AggregationType.WEEKLY, AggregationType.MONTHLY]}
          />
          <SegmentedToggle
            variant="joined"
            options={METRIC_MODE_OPTIONS}
            value={mode}
            onChange={value => setMode(value as LtvMetricMode)}
          />
          <ExportButton
            onExport={handleExport}
            disabled={isLoading || cohortRows.length === 0}
          />
        </div>
      </div>

      <CohortFilters
        filters={filters}
        onChange={setFilters}
        productOptions={productOptions}
        collectionOptions={collectionOptions}
        skuPrefixOptions={skuPrefixOptions}
      />

      {error ? (
        <div className="bg-system-blue-800 rounded-lg p-6 text-states-error">
          Failed to load cohort data. Please try again or adjust the filters.
        </div>
      ) : (
        <>
          <HistoricalAverages
            granularity={granularity}
            cohortRows={cohortRows}
            mode={mode}
            todayIso={todayIso}
          />
          <CohortGrid
            granularity={granularity}
            cohortRows={cohortRows}
            offsets={offsets}
            mode={mode}
            todayIso={todayIso}
            isLoading={isLoading}
          />
          <p className="text-xs text-gray-light-2">
            Cells show accumulated {mode === "total" ? "" : "repeat "}net
            revenue per customer by{" "}
            {granularity === "month" ? "months" : "weeks"} since first order.
            “--” marks periods that have not fully elapsed for that cohort.
            Cohorts include identified customers only; the channel and value
            filters apply to the first order, while accumulated revenue counts
            every later purchase on any channel.
          </p>
        </>
      )}
    </div>
  )
}
